import type {
  AiOperationsCurrentProposal,
  AiOperationsRun,
  AiOperationsRunAction,
  ApprovalProposalDetail,
} from "@adpilot/shared";
import {
  IconArrowBackUp,
  IconChartDots3,
  IconCheck,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ActionImpactSheet } from "@/components/ai-operations/ActionImpactSheet";
import { ControlButton } from "@/components/patterns/control-button";
import { RiskLegendTip } from "@/components/shared/RiskLegendTip";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { rollbackAiRunAction } from "@/lib/ai-run-api";
import { finalizedRunPresentation } from "@/lib/ai-run-presentation";
import { formatAmazonError } from "@/lib/amazon-error";
import { getApiErrorMessage } from "@/lib/api-error";
import { formatSignedCurrencyDiff, useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ActionColumn, ActionGridRow, DecisionBadge } from "./DecisionRecordPrimitives";
import {
  canViewActionImpact,
  finalizedReviewStatus,
  riskTextClass,
  targetTitle,
  targetTypeLabel,
} from "./decision-record-presentation";

export function FinalizedActionRecord({
  actions,
  canRollback,
  onApplied,
  onOpenAnalysis,
  proposal,
  review,
  run,
}: {
  actions: AiOperationsRunAction[];
  canRollback: boolean;
  onApplied: () => void;
  onOpenAnalysis: () => void;
  proposal: AiOperationsCurrentProposal;
  review: ApprovalProposalDetail;
  run: AiOperationsRun;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const approved = review.actions.filter(({ reviewStatus }) => reviewStatus === "approved").length;
  const rejected = review.actions.filter(({ reviewStatus }) => reviewStatus === "rejected").length;
  const expired = review.actions.filter(({ reviewStatus }) => reviewStatus === "expired").length;
  const status = finalizedReviewStatus(run, review, actions, proposal.artifact.actions.length);
  const presentation = finalizedRunPresentation(status, t);
  return (
    <>
      <section className="flex min-h-[67px] items-center justify-between rounded-[10px] border border-line bg-surface px-[18px] py-3.5">
        <div className="flex items-center gap-[11px]">
          <span
            className={cn(
              "flex size-7 items-center justify-center rounded-full",
              presentation.tone === "success"
                ? "bg-green/10 text-green-11"
                : presentation.tone === "danger"
                  ? "bg-red/10 text-red-11"
                  : presentation.tone === "warning"
                    ? "bg-amber/10 text-amber-11"
                    : "bg-subtle text-muted-foreground",
            )}
          >
            {status.outcome === "finalizing" ? (
              <IconRefresh className="size-[13px] animate-spin" />
            ) : presentation.tone === "success" ? (
              <IconCheck className="size-[13px]" />
            ) : (
              <IconX className="size-[13px]" />
            )}
          </span>
          <div>
            <p className="text-sm font-semibold leading-[19px] text-ink">{presentation.title}</p>
            <p className="text-xs leading-4 text-muted-foreground">{presentation.description}</p>
          </div>
        </div>
        {/* 页面状态只说一次(头部徽章+横幅标题);这里只留非零决策计数,
            不再重复一个状态小徽章——单变更运行(常态)三处状态说同一件事。 */}
        <div className="flex items-center gap-3.5">
          {approved > 0 ? (
            <span className="text-xs font-semibold text-green-11">
              {t("runDetail.decisionRecord.finalizing.approved", { count: approved })}
            </span>
          ) : null}
          {rejected > 0 ? (
            <span className="text-xs font-semibold text-muted-foreground">
              {t("runDetail.decisionRecord.finalizing.rejected", { count: rejected })}
            </span>
          ) : null}
          {expired > 0 ? (
            <span className="text-xs font-semibold text-amber-11">
              {t("runDetail.decisionRecord.finalizing.expired", { count: expired })}
            </span>
          ) : null}
        </div>
      </section>
      <div className="flex min-h-[22px] items-center justify-between px-0.5 py-px">
        <div className="flex min-w-0 items-center gap-3">
          <span className="shrink-0 text-xs font-medium text-muted-foreground">
            {t("runDetail.decisionRecord.summary.label")}
          </span>
          <p className="truncate text-sm font-semibold leading-5 text-ink">
            {proposal.artifact.analysis.summary}
          </p>
        </div>
        <ControlButton controlSize="compact" variant="outline" onClick={onOpenAnalysis}>
          {t("runDetail.decisionRecord.summary.fullAnalysis")}
        </ControlButton>
      </div>
      <FinalizedActionsTable
        actions={actions}
        canRollback={canRollback}
        proposal={proposal}
        review={review}
        runId={run.id}
        onApplied={onApplied}
      />
    </>
  );
}

function FinalizedActionsTable({
  actions,
  canRollback,
  onApplied,
  proposal,
  review,
  runId,
}: {
  actions: AiOperationsRunAction[];
  canRollback: boolean;
  onApplied: () => void;
  proposal: AiOperationsCurrentProposal;
  review: ApprovalProposalDetail;
  runId: string;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const [rollbackTarget, setRollbackTarget] = useState<
    AiOperationsCurrentProposal["artifact"]["actions"][number] | null
  >(null);
  const [rollbackPending, setRollbackPending] = useState(false);
  const [impactTarget, setImpactTarget] = useState<{ id: string; targetName: string } | null>(null);
  const actionState = new Map(actions.map((action) => [action.id, action]));
  const rollbackByOriginalId = new Map(
    actions
      .filter((action) => action.rollbackOfActionId !== null)
      .map((action) => [action.rollbackOfActionId as string, action]),
  );

  async function confirmRollback(): Promise<void> {
    if (!rollbackTarget) return;
    try {
      setRollbackPending(true);
      await rollbackAiRunAction(runId, rollbackTarget.id);
      setRollbackTarget(null);
      onApplied();
    } catch (error) {
      toast.error(await getApiErrorMessage(error));
    } finally {
      setRollbackPending(false);
    }
  }
  return (
    <section className="overflow-hidden rounded-[10px] border border-line bg-surface">
      <div className="flex min-h-[49px] items-center justify-between border-b border-line px-4 py-3.5">
        <h2 className="text-base font-semibold leading-5 text-ink">
          {t("runDetail.decisionRecord.actions.title")}
        </h2>
        <span className="text-xs text-muted-foreground">
          {t("runDetail.decisionRecord.actions.count", { count: proposal.artifact.actions.length })}
        </span>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[1080px]">
          <ActionGridRow header>
            <ActionColumn>{t("runDetail.decisionRecord.actions.columns.target")}</ActionColumn>
            <ActionColumn>{t("runDetail.decisionRecord.actions.columns.bidChange")}</ActionColumn>
            <ActionColumn>{t("runDetail.decisionRecord.actions.columns.aiReason")}</ActionColumn>
            <ActionColumn className="flex items-center gap-1">
              {t("runDetail.decisionRecord.actions.columns.guardrail")}
              <RiskLegendTip
                thresholds={proposal.artifact.validation.effectivePolicy.bidChangeRiskThresholds}
              />
            </ActionColumn>
            <ActionColumn className="text-right">
              {t("runDetail.decisionRecord.actions.columns.outcome")}
            </ActionColumn>
          </ActionGridRow>
          {proposal.artifact.actions.map((action) => (
            <FinalizedActionRow
              key={action.id}
              action={action}
              actionState={actionState.get(action.id)}
              review={review}
              rollback={rollbackByOriginalId.get(action.id)}
              canRollback={canRollback}
              onOpenImpact={() =>
                setImpactTarget({ id: action.id, targetName: targetTitle(action, t) })
              }
              onRequestRollback={() => setRollbackTarget(action)}
            />
          ))}
        </div>
      </div>
      <Dialog
        open={rollbackTarget !== null}
        onOpenChange={(open) => {
          if (!open && !rollbackPending) setRollbackTarget(null);
        }}
      >
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>{t("runDetail.decisionRecord.rollbackAction.confirmTitle")}</DialogTitle>
            <DialogDescription>
              {rollbackTarget
                ? t("runDetail.decisionRecord.rollbackAction.confirmDescription", {
                    target: targetTitle(rollbackTarget, t),
                    from: rollbackTarget.server.newBid.toFixed(2),
                    to: rollbackTarget.server.oldBid.toFixed(2),
                  })
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" disabled={rollbackPending} />}>
              {t("common:actions.cancel")}
            </DialogClose>
            <Button
              loading={rollbackPending}
              disabled={rollbackPending}
              onClick={() => void confirmRollback()}
            >
              {t("runDetail.decisionRecord.rollbackAction.confirm")}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
      <ActionImpactSheet
        actionId={impactTarget?.id ?? null}
        targetName={impactTarget?.targetName ?? ""}
        open={impactTarget !== null}
        onOpenChange={(open) => {
          if (!open) setImpactTarget(null);
        }}
      />
    </section>
  );
}

function FinalizedActionRow({
  action,
  actionState,
  canRollback,
  onOpenImpact,
  onRequestRollback,
  review,
  rollback,
}: {
  action: AiOperationsCurrentProposal["artifact"]["actions"][number];
  actionState?: AiOperationsRunAction;
  canRollback: boolean;
  onOpenImpact: () => void;
  onRequestRollback: () => void;
  review: ApprovalProposalDetail;
  rollback?: AiOperationsRunAction;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { formatCurrency } = useFormatters();
  const reviewAction = review.actions.find(({ id }) => id === action.id);
  const decision = [...review.decisionHistory]
    .reverse()
    .find(
      ({ actionId, decision, phase }) =>
        actionId === action.id && decision !== "finalize" && phase === "applied",
    );
  const rejected = reviewAction?.reviewStatus === "rejected";
  const finalBid = rejected
    ? action.server.oldBid
    : decision?.decision === "modify_approve" && decision.decidedBid !== null
      ? decision.decidedBid
      : action.server.newBid;
  const bidCaption = rejected
    ? t("runDetail.decisionRecord.actions.unchanged")
    : decision?.decision === "modify_approve"
      ? // "已修改"要回答的是改之前 AI 建议的值;差值主行的 old→new 已经说了。
        t("runDetail.decisionRecord.actions.modifiedFrom", {
          bid: formatCurrency(action.server.newBid),
        })
      : formatSignedCurrencyDiff(action.server.oldBid, finalBid, formatCurrency);
  const target = review.targets.find(({ id }) => id === action.target.id);
  // 审批通过但执行失败时,结局必须显示"失败"而不是"已批准"——reviewStatus
  // 只反映人的决定,掩盖 Amazon 写入结果(2026-08-29 生产 400 事故的展示盲区)。
  const executionFailed = reviewAction?.status === "failed" || actionState?.status === "failed";
  // 回滚状态取代原结局徽章,一行只呈现一个状态(回滚中/已回滚/回滚失败)。
  const rollbackState = rollback
    ? rollback.status === "executed"
      ? "rolled_back"
      : rollback.status === "failed" || rollback.status === "skipped"
        ? "rollback_failed"
        : "rolling_back"
    : null;
  const outcome = executionFailed
    ? "failed"
    : (rollbackState ?? reviewAction?.reviewStatus ?? actionState?.status ?? "not_required");
  const executionError = reviewAction?.amazonError ?? null;
  return (
    <ActionGridRow>
      <ActionColumn>
        <p className="truncate text-[13px] font-semibold leading-[18px] text-ink">
          {targetTitle(action, t)}
        </p>
        <p className="mt-[3px] text-xs leading-4 text-muted-foreground">
          {targetTypeLabel(action, target?.keywordMatchType, t)}
        </p>
      </ActionColumn>
      <ActionColumn>
        <p className="text-[13px] font-semibold leading-[18px] text-ink">
          {rejected
            ? formatCurrency(finalBid)
            : `${formatCurrency(action.server.oldBid)} → ${formatCurrency(finalBid)}`}
        </p>
        <p
          className={cn(
            "mt-0.5 text-xs leading-4",
            rejected
              ? "text-muted-foreground"
              : finalBid >= action.server.oldBid
                ? "text-green-11"
                : "text-red-11",
          )}
        >
          {bidCaption}
        </p>
      </ActionColumn>
      <ActionColumn>
        <p className="whitespace-pre-wrap break-words text-xs leading-[18px] text-ink">
          {action.agent.reasoning}
        </p>
      </ActionColumn>
      <ActionColumn>
        <p className={cn("text-xs font-semibold", riskTextClass(action.server.riskLevel))}>
          {t(`runDetail.decisionRecord.risk.${action.server.riskLevel}`)}
        </p>
      </ActionColumn>
      <ActionColumn className="flex flex-col items-end gap-1">
        <DecisionBadge status={outcome} />
        {/* 执行失败时把 Amazon 返回的错误直接亮出来,不让用户猜。 */}
        {executionFailed && executionError ? (
          <p
            className="line-clamp-3 max-w-[280px] whitespace-normal break-words text-right text-[11px] leading-4 text-red-11"
            title={executionError}
          >
            {formatAmazonError(executionError)}
          </p>
        ) : null}
        {canViewActionImpact(actionState) ? (
          <ControlButton controlSize="compact" variant="outline" onClick={onOpenImpact}>
            <IconChartDots3 data-icon="inline-start" />
            {t("runDetail.decisionRecord.impact.open")}
          </ControlButton>
        ) : null}
        {!rollback && canRollback && actionState?.status === "executed" ? (
          <ControlButton controlSize="compact" variant="outline" onClick={onRequestRollback}>
            <IconArrowBackUp data-icon="inline-start" />
            {t("runDetail.decisionRecord.rollbackAction.button")}
          </ControlButton>
        ) : null}
      </ActionColumn>
    </ActionGridRow>
  );
}
